import { httpClient, toClientApiError } from "./httpClient";

export type EzcountDocTypeOption = { value: number; label: string };

export type EzcountConnectionStatus = {
  configured: boolean;
  isEnabled: boolean;
  environment: "demo" | "prod";
  hasApiKey: boolean;
  apiKeyMasked: string;
  apiEmail: string | null;
  developerEmail: string | null;
  developerPhone: string | null;
  hasDevMasterKey: boolean;
  devMasterKeyMasked: string;
  defaultDocType: number;
  defaultDocTypeLabel: string;
  autoIssueOnIncome: boolean;
  dontSendEmail: boolean;
  sendCopy: boolean;
  docTypeOptions: EzcountDocTypeOption[];
};

export type EzcountDocument = {
  id: string;
  tenantId: string;
  customerId: string | null;
  incomeId: string | null;
  receiptId: string | null;
  docType: number;
  ezcountDocUuid: string | null;
  docNumber: string | null;
  pdfUrl: string | null;
  status: "pending" | "success" | "failed";
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export const ezcountApi = {
  getConnection() {
    return wrap(async () => {
      const { data } = await httpClient.get<{ item: EzcountConnectionStatus }>(
        "/settings/ezcount-connection",
      );
      return data.item;
    });
  },

  saveConnection(body: {
    isEnabled?: boolean;
    environment?: "demo" | "prod";
    apiKey?: string | null;
    clearApiKey?: boolean;
    apiEmail?: string | null;
    developerEmail?: string | null;
    developerPhone?: string | null;
    devMasterKey?: string | null;
    clearDevMasterKey?: boolean;
    defaultDocType?: number;
    autoIssueOnIncome?: boolean;
    dontSendEmail?: boolean;
    sendCopy?: boolean;
  }) {
    return wrap(async () => {
      const { data } = await httpClient.put<{ item: EzcountConnectionStatus }>(
        "/settings/ezcount-connection",
        body,
      );
      return data.item;
    });
  },

  testConnection() {
    return wrap(async () => {
      const { data } = await httpClient.post<{
        item: {
          ok: boolean;
          docUuid: string | null;
          docNumber: string | null;
          pdfUrl: string | null;
          docType: number;
        };
      }>("/settings/ezcount-connection/test", {});
      return data.item;
    });
  },

  listDocuments(params?: { customerId?: string; status?: string }) {
    return wrap(async () => {
      const { data } = await httpClient.get<{ items: EzcountDocument[] }>(
        "/ezcount/documents",
        { params },
      );
      return data.items;
    });
  },

  importDocument(body: {
    urlOrUuid: string;
    customerId?: string | null;
    title?: string;
    docType?: number;
  }) {
    return wrap(async () => {
      const { data } = await httpClient.post<{ item: EzcountDocument }>(
        "/ezcount/documents/import",
        body,
      );
      return data.item;
    });
  },

  getDocumentPdfUrl(documentId: string) {
    return wrap(async () => {
      const { data } = await httpClient.get<{
        item: { pdfUrl: string; docUuid: string | null; docNumber: string | null };
      }>(`/ezcount/documents/${documentId}/pdf-url`);
      return data.item;
    });
  },
};
